/**
 * Data Fetcher - Recupera dati dal DB basandosi sull'intento
 *
 * Usa gli store Zustand esistenti per recuperare i dati già caricati
 * da passare al LLM come contesto.
 */

import { useProjectsStore } from '../../stores/projectsStore';
import type { DetectedIntent, Domain } from './intentDetector';
import type { Project, Task } from '../../domain/models';

export interface FetchedData {
  domain: Domain;
  data: unknown;
  summary: string; // Descrizione testuale per il prompt
  count: number;
}

// =============================================================================
// DATA FETCHERS
// =============================================================================

/**
 * Recupera progetti dallo store Zustand (già caricati all'avvio)
 */
async function fetchProjects(intent: DetectedIntent): Promise<FetchedData> {
  try {
    console.log('[DataFetcher] Fetching projects from store...');

    // Use existing store state (already loaded at app init)
    const storeState = useProjectsStore.getState();
    let projects = [...storeState.projects];

    console.log('[DataFetcher] Projects in store:', projects.length);

    // Per ora: "attivi" = non chiusi, "chiusi" = chiusi
    // TODO: rivedere quando il modello avrà status semplificato (attivo/archiviato) + condizione calcolata (ok/a-rischio/in-ritardo)
    if (intent.filters?.status === 'completed') {
      projects = projects.filter(p => p.status === 'chiuso');
      console.log('[DataFetcher] Filtered to closed projects:', projects.length);
    } else if (intent.filters?.status === 'active') {
      // "attivi" = tutto tranne chiuso
      projects = projects.filter(p => p.status !== 'chiuso');
      console.log('[DataFetcher] Filtered to active (non-closed) projects:', projects.length);
    }
    // Se nessun filtro o filtro non riconosciuto, mostra tutti

    // Create text summary for the LLM
    let summary = '';
    if (projects.length === 0) {
      summary = 'Non ci sono progetti nel database.';
    } else {
      const statusFilter = intent.filters?.status ? ` con status "${intent.filters.status}"` : '';
      summary = `Trovati ${projects.length} progetti${statusFilter}:\n\n`;
      summary += projects.map((p, i) => {
        const details = [
          `${i + 1}. **${p.title}**`,
          `   - Status: ${p.status}`,
          `   - Priorità: ${p.priority}`,
          p.clientName ? `   - Cliente: ${p.clientName}` : null,
          p.dueAt ? `   - Scadenza: ${new Date(p.dueAt).toLocaleDateString('it-IT')}` : null,
          p.progress !== undefined ? `   - Progresso: ${p.progress}%` : null,
        ].filter(Boolean).join('\n');
        return details;
      }).join('\n\n');
    }

    return {
      domain: 'projects',
      data: projects,
      summary,
      count: projects.length
    };
  } catch (error) {
    console.error('[DataFetcher] Error fetching projects:', error);
    return {
      domain: 'projects',
      data: [],
      summary: 'Errore nel recupero dei progetti dal database: ' + (error instanceof Error ? error.message : 'errore sconosciuto'),
      count: 0
    };
  }
}

/**
 * Recupera task dallo store Zustand
 */
async function fetchTasks(intent: DetectedIntent): Promise<FetchedData> {
  try {
    console.log('[DataFetcher] Fetching tasks from store...');

    // Use existing store state
    const storeState = useProjectsStore.getState();
    const projects = storeState.projects;

    // Get tasks and add project title for context
    let allTasks: (Task & { _projectTitle?: string })[] = storeState.tasks.map(t => {
      const project = projects.find(p => p.id === t.projectId);
      return { ...t, _projectTitle: project?.title };
    });

    console.log('[DataFetcher] Tasks in store:', allTasks.length);

    // Apply status filter if requested
    if (intent.filters?.status) {
      const statusMap: Record<string, string> = {
        active: 'in_progress',
        completed: 'done',
        planning: 'todo'
      };
      const targetStatus = statusMap[intent.filters.status] || intent.filters.status;
      allTasks = allTasks.filter(t => t.status === targetStatus || (intent.filters?.status === 'completed' && t.done));
      console.log('[DataFetcher] After status filter:', allTasks.length);
    }

    let summary = '';
    if (allTasks.length === 0) {
      summary = 'Non ci sono task nel database.';
    } else {
      summary = `Trovati ${allTasks.length} task:\n\n`;
      summary += allTasks.slice(0, 20).map((t: Task & { _projectTitle?: string }, i) => {
        return [
          `${i + 1}. **${t.title}**`,
          t._projectTitle ? `   - Progetto: ${t._projectTitle}` : null,
          `   - Status: ${t.status}${t.done ? ' (completato)' : ''}`,
          t.priority ? `   - Priorità: ${t.priority}` : null,
          t.dueAt ? `   - Scadenza: ${new Date(t.dueAt).toLocaleDateString('it-IT')}` : null,
        ].filter(Boolean).join('\n');
      }).join('\n\n');

      if (allTasks.length > 20) {
        summary += `\n\n...e altri ${allTasks.length - 20} task.`;
      }
    }

    return {
      domain: 'tasks',
      data: allTasks,
      summary,
      count: allTasks.length
    };
  } catch (error) {
    console.error('[DataFetcher] Error fetching tasks:', error);
    return {
      domain: 'tasks',
      data: [],
      summary: 'Errore nel recupero dei task dal database: ' + (error instanceof Error ? error.message : 'errore sconosciuto'),
      count: 0
    };
  }
}

/**
 * Recupera eventi dal calendario (placeholder - da implementare con repository calendario)
 */
async function fetchEvents(_intent: DetectedIntent): Promise<FetchedData> {
  // TODO: Use GraphQLCalendarRepository when available
  return {
    domain: 'calendar',
    data: [],
    summary: 'Il recupero degli eventi dal calendario non è ancora implementato nel Runner.',
    count: 0
  };
}

/**
 * Recupera contatti (placeholder - da implementare con repository hub)
 */
async function fetchContacts(_intent: DetectedIntent): Promise<FetchedData> {
  // TODO: Use GraphQLHubRepository when available
  return {
    domain: 'contacts',
    data: [],
    summary: 'Il recupero dei contatti non è ancora implementato nel Runner.',
    count: 0
  };
}

// =============================================================================
// MAIN FETCHER
// =============================================================================

/**
 * Recupera dati dal database basandosi sull'intento rilevato
 */
export async function fetchDataForIntent(intent: DetectedIntent): Promise<FetchedData | null> {
  console.log('[DataFetcher] Fetching data for intent:', intent.domain, intent.operation);

  switch (intent.domain) {
    case 'projects':
      return fetchProjects(intent);
    case 'tasks':
      return fetchTasks(intent);
    case 'calendar':
      return fetchEvents(intent);
    case 'contacts':
      return fetchContacts(intent);
    case 'accounting':
      return {
        domain: 'accounting',
        data: [],
        summary: 'La sezione contabilità non è ancora implementata nel Runner.',
        count: 0
      };
    default:
      return null;
  }
}
